import { useMemo } from 'react';
import PropTypes from 'prop-types';
import { Box, Typography } from '@mui/material';

export default function RevenueDistributionChart({ data, title, totalLeads }) {
    // Aggregate revenue by category/treatment across all practices
    const chartData = useMemo(() => {
        if (!data?.length) return [];

        const revenueMap = {};
        const colorMap = {};
        let totalRevenue = 0;

        data.forEach((practice) => {
            practice.distribution?.forEach((entry) => {
                const key = entry.categoryName || entry.categoryId;
                if (!key) return;

                if (!revenueMap[key]) {
                    revenueMap[key] = 0;
                }
                revenueMap[key] += entry.revenue || 0;
                totalRevenue += entry.revenue || 0;

                if (entry.color && !colorMap[key]) {
                    colorMap[key] = entry.color;
                }
            });
        });

        return Object.entries(revenueMap)
            .filter(([name]) => name && name !== 'Unknown')
            .map(([name, revenue]) => ({
                name,
                revenue,
                color: colorMap[name] || '#6366f1',
                percentage: totalRevenue > 0 ? ((revenue / totalRevenue) * 100).toFixed(1) : 0,
            }))
            .sort((a, b) => b.revenue - a.revenue);
    }, [data]);

    const totalRevenue = chartData.reduce((sum, item) => sum + item.revenue, 0);
    const revenuePerLead = totalLeads > 0 ? (totalRevenue / totalLeads).toFixed(2) : '0.00';

    if (!chartData.length) {
        return (
            <Box
                sx={{
                    bgcolor: '#fff',
                    borderRadius: '16px',
                    p: '24px',
                    boxShadow: '0 1px 3px rgba(0,0,0,0.1)',
                    textAlign: 'center',
                }}
            >
                <Typography sx={{ fontSize: '18px', fontWeight: 600, mb: 2 }}>{title}</Typography>
                <Typography sx={{ color: '#64748b' }}>No revenue data available</Typography>
            </Box>
        );
    }

    const maxRevenue = chartData?.length ? Math.max(...chartData.map((d) => d.revenue)) : 0;

    return (
        <Box
            sx={{
                bgcolor: '#fff',
                borderRadius: '16px',
                p: '24px',
                boxShadow: '0 1px 3px rgba(0,0,0,0.1)',
            }}
        >
            {/* Header */}
            <Typography sx={{ fontSize: '18px', fontWeight: 600, mb: 1 }}>{title}</Typography>

            {/* Summary */}
            <Box sx={{ display: 'flex', gap: 3, mb: 3, flexWrap: 'wrap' }}>
                <Typography sx={{ fontSize: '13px', color: '#64748b' }}>
                    Total: <strong style={{ color: '#10b981' }}>£{totalRevenue.toLocaleString()}</strong>
                </Typography>
                <Typography sx={{ fontSize: '13px', color: '#64748b' }}>
                    Per Lead: <strong style={{ color: '#3b82f6' }}>£{revenuePerLead}</strong>
                </Typography>
                <Typography sx={{ fontSize: '13px', color: '#64748b' }}>
                    Categories: <strong>{chartData.length}</strong>
                </Typography>
            </Box>

            {/* Donut Chart Representation using CSS */}
            <Box sx={{ display: 'flex', gap: 3, alignItems: 'flex-start' }}>
                {/* Simple donut visualization */}
                <Box
                    sx={{
                        width: 120,
                        height: 120,
                        borderRadius: '50%',
                        background: `conic-gradient(${chartData
                            .map((item, index) => {
                                const startPercent = chartData
                                    .slice(0, index)
                                    .reduce((sum, i) => sum + parseFloat(i.percentage), 0);
                                const endPercent = startPercent + parseFloat(item.percentage);
                                return `${item.color} ${startPercent}% ${endPercent}%`;
                            })
                            .join(', ')})`,
                        position: 'relative',
                        flexShrink: 0,
                        '&::after': {
                            content: '""',
                            position: 'absolute',
                            top: '50%',
                            left: '50%',
                            transform: 'translate(-50%, -50%)',
                            width: '60%',
                            height: '60%',
                            borderRadius: '50%',
                            bgcolor: '#fff',
                        },
                    }}
                />

                {/* Legend */}
                <Box sx={{ flex: 1, maxHeight: 200, overflowY: 'auto' }}>
                    {chartData.slice(0, 8).map((item) => (
                        <Box
                            key={item.name}
                            sx={{
                                display: 'flex',
                                alignItems: 'center',
                                justifyContent: 'space-between',
                                mb: 1,
                                pb: 1,
                                borderBottom: '1px solid #f1f5f9',
                            }}
                        >
                            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, flex: 1, minWidth: 0 }}>
                                <Box
                                    sx={{
                                        width: 10,
                                        height: 10,
                                        borderRadius: '2px',
                                        bgcolor: item.color,
                                        flexShrink: 0,
                                    }}
                                />
                                <Typography
                                    sx={{
                                        fontSize: '12px',
                                        fontWeight: 500,
                                        overflow: 'hidden',
                                        textOverflow: 'ellipsis',
                                        whiteSpace: 'nowrap',
                                    }}
                                    title={item.name}
                                >
                                    {item.name}
                                </Typography>
                            </Box>
                            <Typography sx={{ fontSize: '12px', fontWeight: 600, color: '#10b981', ml: 1 }}>
                                £{item.revenue.toLocaleString()}
                            </Typography>
                        </Box>
                    ))}
                    {chartData.length > 8 && (
                        <Typography sx={{ fontSize: '11px', color: '#94a3b8', mt: 1 }}>
                            +{chartData.length - 8} more categories
                        </Typography>
                    )}
                </Box>
            </Box>

            {/* Bar representation for top items */}
            <Box sx={{ mt: 3, pt: 2, borderTop: '1px solid #e2e8f0' }}>
                <Typography sx={{ fontSize: '13px', fontWeight: 600, color: '#64748b', mb: 2 }}>
                    Revenue Distribution
                </Typography>
                {chartData.slice(0, 5).map((item) => (
                    <Box key={item.name} sx={{ mb: 1.5 }}>
                        <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 0.5 }}>
                            <Typography sx={{ fontSize: '12px', fontWeight: 500 }}>{item.name}</Typography>
                            <Typography sx={{ fontSize: '12px', color: '#64748b' }}>
                                {item.percentage}%
                            </Typography>
                        </Box>
                        <Box
                            sx={{
                                height: 8,
                                bgcolor: '#f1f5f9',
                                borderRadius: 4,
                                overflow: 'hidden',
                            }}
                        >
                            <Box
                                sx={{
                                    height: '100%',
                                    width: `${(item.revenue / maxRevenue) * 100}%`,
                                    bgcolor: item.color,
                                    borderRadius: 4,
                                    transition: 'width 0.5s ease',
                                }}
                            />
                        </Box>
                    </Box>
                ))}
            </Box>
        </Box>
    );
}

RevenueDistributionChart.propTypes = {
    data: PropTypes.arrayOf(
        PropTypes.shape({
            practiceId: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
            distribution: PropTypes.arrayOf(
                PropTypes.shape({
                    categoryId: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
                    categoryName: PropTypes.string,
                    revenue: PropTypes.number,
                    color: PropTypes.string,
                })
            ),
            totalRevenue: PropTypes.number,
        })
    ),
    title: PropTypes.string,
    totalLeads: PropTypes.number,
};

RevenueDistributionChart.defaultProps = {
    data: [],
    title: 'Revenue Distribution',
    totalLeads: 0,
};
